import { Fragment, useEffect, useMemo, useRef } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { HorizontalOverflow } from '../../../components/HorizontalOverflow';
import {
  Alert,
  Anchor,
  BreakoutWrapper,
  Flex,
  Heading,
  Link,
  Loading,
  Table,
  Td,
  Text,
  Th,
  Tr
} from '../../../components';
import {
  BarchartAlt as BarchartIcon,
  Station as StationIcon
} from '@urbaninfrastructure/react-icons';
import {
  tripsQuery,
  tripsQuery_trips,
  tripsQueryVariables
} from '../../../core-types';
import { gql, useQuery } from '@apollo/client';
import { PayUnpaidOrders } from '../../UnpaidOrders/PayUnpaidOrders';
import { isSameMonth, parseISO } from 'date-fns';
import { TablePager, useTablePager } from '../../../components/TablePager';
import {
  TripDistance,
  TripDuration,
  TripIcon,
  TripRating,
  TripStartDate,
  TripStation
} from './components';

export const TRIPS_QUERY = gql`
  query tripsQuery($options: QueryOptions) {
    trips(options: $options) {
      id
      startedAt
      state
      endedAt
      duration
      distance
      userRating
      startDock {
        id
        dockGroup {
          id
          title
        }
      }
      endDock {
        id
        dockGroup {
          id
          title
        }
      }
    }
    personalSummary {
      numberOfTrips
      stationsVisited
    }
  }
`;

const PER_PAGE = 25;

const StatisticBlock = ({
  textId,
  textDefaultMessage,
  value,
  icon,
  containerProps = {}
}: {
  textId: string;
  textDefaultMessage: string;
  value: any;
  icon: any;
  containerProps?: object;
}) => {
  return (
    <Flex flexDirection="column" alignItems="center" {...containerProps}>
      <Text mb={1}>
        <FormattedMessage id={textId} defaultMessage={textDefaultMessage} />
      </Text>
      <Flex alignItems="center">
        {icon}
        <Text>{value}</Text>
      </Flex>
    </Flex>
  );
};

const DEFAULT_TRIP_QUERY_VARIABLES = (page = 1): tripsQueryVariables => ({
  options: {
    order: 'asc',
    orderBy: 'startedAt',
    perPage: PER_PAGE,
    page,
    showDeleted: false
  }
});

export const Trips = () => {
  const { data, loading, refetch } = useQuery<tripsQuery, tripsQueryVariables>(
    TRIPS_QUERY,
    {
      ssr: false,
      variables: DEFAULT_TRIP_QUERY_VARIABLES()
    }
  );

  const trips = useMemo<tripsQuery_trips[]>(() => {
    if (!data?.trips?.length) return [];

    // @ts-ignore
    const trips: tripsQuery_trips[] = data.trips.filter(Boolean);

    return trips;
  }, [data?.trips]);
  const stationsVisited = data?.personalSummary?.stationsVisited || 0;
  const numberOfTrips = data?.personalSummary?.numberOfTrips || 0;

  const hasTrips = Boolean(numberOfTrips);

  const { page, openPrevPage, openNextPage, offset } = useTablePager({
    perPage: PER_PAGE
  });
  const isPrevDisabled = loading || page === 1;
  const isNextDisabled = loading || offset + PER_PAGE > numberOfTrips;
  const isNeedPagination = numberOfTrips > PER_PAGE;

  const changePageAnchorElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    refetch(DEFAULT_TRIP_QUERY_VARIABLES(page));
    changePageAnchorElementRef?.current?.focus();
  }, [page]);

  return (
    <>
      <Heading textAlign="center">
        <FormattedMessage id="trip_history" defaultMessage="Trip history" />
      </Heading>
      {loading && <Loading justifyContent="center" />}
      {!loading && (
        <>
          <PayUnpaidOrders />
          {!hasTrips && (
            <Alert variant="info">
              <FormattedMessage
                id="modules.Profile.Trips.noTrips"
                defaultMessage="Your trips will show up here"
              />
            </Alert>
          )}
          <div ref={changePageAnchorElementRef} tabIndex={0} />
          {hasTrips && (
            <BreakoutWrapper>
              <Flex justifyContent="center" mb={5}>
                <StatisticBlock
                  textId="total_trips"
                  textDefaultMessage="Total trips"
                  value={numberOfTrips}
                  icon={<BarchartIcon size={25} mr={3} color="primary" />}
                />
                <StatisticBlock
                  textId="stations_visited"
                  textDefaultMessage="Stations visited"
                  value={stationsVisited}
                  icon={<StationIcon size={17} mr={3} color="primary" />}
                  containerProps={{
                    ml: 50
                  }}
                />
              </Flex>
              <HorizontalOverflow mb={1}>
                <Table data-testid="Trips__Table" variant="simple">
                  <thead>
                    <Tr>
                      <Th />
                      <Th>
                        <FormattedMessage id="date" defaultMessage="Date" />
                      </Th>
                      <Th>
                        <FormattedMessage id="start" defaultMessage="Start" />
                      </Th>
                      <Th>
                        <FormattedMessage id="end" defaultMessage="End" />
                      </Th>
                      <Th>
                        <FormattedMessage
                          id="duration"
                          defaultMessage="Duration"
                        />
                      </Th>
                      <Th>
                        <FormattedMessage
                          id="distance"
                          defaultMessage="Distance"
                        />
                      </Th>
                      <Th>
                        <FormattedMessage
                          id="TripDetails.tripRating"
                          defaultMessage="Rating"
                        />
                      </Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {trips.map((trip, i) => {
                      const prevTrip = trips[i - 1];
                      return (
                        <Fragment key={trip.id}>
                          {trip.startedAt &&
                            (!prevTrip?.startedAt ||
                              !isSameMonth(
                                parseISO(trip.startedAt),
                                parseISO(prevTrip.startedAt)
                              )) && (
                              <Tr>
                                <Td />
                                <Td colSpan={6}>
                                  <Text small bold>
                                    <FormattedDate
                                      value={trip.startedAt}
                                      month="long"
                                      year="numeric"
                                    />
                                  </Text>
                                </Td>
                              </Tr>
                            )}
                          <Tr>
                            <Td
                              style={{
                                width: '44px',
                                paddingRight: 0,
                                verticalAlign: 'middle'
                              }}
                            >
                              <Link
                                route={'profile.trips.trip'}
                                params={{ tripId: trip.id }}
                                passHref
                              >
                                <Anchor>
                                  <TripIcon
                                    state={trip.state}
                                    data-testid={`Trips__Table__Row__${trip.id}__Icon`}
                                  />
                                </Anchor>
                              </Link>
                            </Td>
                            <Td>
                              <Link
                                route={'profile.trips.trip'}
                                params={{ tripId: trip.id }}
                                passHref
                              >
                                <Anchor>
                                  <TripStartDate startedAt={trip.startedAt} />
                                </Anchor>
                              </Link>
                            </Td>
                            <Td>
                              <TripStation
                                date={trip.startedAt}
                                title={trip.startDock?.dockGroup?.title}
                              />
                            </Td>
                            <Td>
                              <TripStation
                                date={trip.endedAt}
                                title={trip.endDock?.dockGroup?.title}
                              />
                            </Td>
                            <Td verticalAlign="center">
                              <TripDuration duration={trip.duration} />
                            </Td>
                            <Td verticalAlign="center">
                              <TripDistance distance={trip.distance} />
                            </Td>
                            <Td verticalAlign="middle">
                              <TripRating userRating={trip.userRating} />
                            </Td>
                          </Tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </HorizontalOverflow>
              {isNeedPagination && (
                <TablePager
                  page={page}
                  onPrevClick={openPrevPage}
                  isPrevDisabled={isPrevDisabled}
                  onNextClick={openNextPage}
                  isNextDisabled={isNextDisabled}
                  buttonGroupProps={{
                    style: { marginTop: '1em' }
                  }}
                />
              )}
            </BreakoutWrapper>
          )}
        </>
      )}
    </>
  );
};
